

















import { VueConstructor } from 'vue'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import getTextFieldComContador from '@/components/shared/textFieldComContador'
import { contarPosicoesDaMascara, formatarCarteirinha, getTamanhoMaximoCarteirinha, isMascaraCarteirinhaValida, normalizarCarteirinha, removerSeparadoresCarteirinha, validateCarteirinhaComMascara } from '@/utils/mascaraCarteirinha'

type RegraCampo = (valor: string) => true | string

// Com máscara, o contador ignora os separadores: conta só o que é gravado (letras e dígitos).
const contarLetrasEDigitos = (valor: string): number => removerSeparadoresCarteirinha(valor).length

// Campo de carteirinha com a máscara do convênio: exibe com separadores e emite o v-model só com letras e dígitos.
@Component({ inheritAttrs: false })
export default class InputCarteirinha extends Vue {
  $refs!: {
    campo?: Vue & { validate: (force?: boolean) => boolean }
  }

  @Prop({ type: String, default: null }) value: string | null

  @Prop({ type: String, default: '' }) mascara: string

  // type: Boolean é o que faz o atributo sem valor (<input-carteirinha counter>) virar true.
  @Prop({ type: Boolean, default: false }) disabled: boolean

  @Prop({ type: Array, default: () => [] }) rules: RegraCampo[]

  @Prop({ type: Boolean, default: false }) counter: boolean

  @Prop({ type: [Number, String] }) tamanhoMaximoSemMascara?: number | string

  get campoDeTexto(): VueConstructor {
    return getTextFieldComContador()
  }

  // Só em campo editável: desabilitado, a máscara esconderia caracteres de uma carteirinha já gravada fora do formato.
  get mascaraAtiva(): string {
    if (this.disabled || !isMascaraCarteirinhaValida(this.mascara)) {
      return ''
    }

    return this.mascara
  }

  get valorExibido(): string | null {
    if (!this.mascaraAtiva || !this.value) {
      return this.value
    }

    return formatarCarteirinha(this.value, this.mascaraAtiva)
  }

  get tamanhoMaximo(): number {
    return getTamanhoMaximoCarteirinha(this.mascaraAtiva, this.tamanhoMaximoSemMascara)
  }

  // Com máscara, o máximo do contador são as posições digitáveis, igual à mensagem de validação.
  get contador(): number | false {
    if (!this.counter) {
      return false
    }

    if (this.mascaraAtiva) {
      return contarPosicoesDaMascara(this.mascaraAtiva)
    }

    return this.tamanhoMaximo
  }

  get contagemDoContador(): ((valor: string) => number) | undefined {
    if (!this.mascaraAtiva) {
      return undefined
    }

    return contarLetrasEDigitos
  }

  get regras(): RegraCampo[] {
    if (!this.mascaraAtiva) {
      return this.rules
    }

    return [...this.rules, (valor: string) => validateCarteirinhaComMascara(valor, this.mascaraAtiva)]
  }

  get listenersDoCampo() {
    const listeners = { ...this.$listeners }
    delete listeners.input

    return listeners
  }

  onInput(valorDigitado: string) {
    if (!this.mascaraAtiva) {
      this.$emit('input', valorDigitado)
      return
    }

    this.$emit('input', normalizarCarteirinha(valorDigitado || '', this.mascaraAtiva))
  }

  // Valor vindo de fora (colar, busca de paciente, dado antigo) também fica sem separador e dentro da máscara.
  @Watch('value')
  onChangeValue() {
    this.normalizarValor()
  }

  // Na troca de convênio a carteirinha é reformatada e revalidada: o Vuetify não revalida sozinho quando só a regra muda.
  @Watch('mascaraAtiva', { immediate: true })
  onChangeMascaraAtiva() {
    this.normalizarValor()

    if (!this.mascaraAtiva || !this.value) {
      return
    }

    this.$nextTick(() => {
      if (this.$refs.campo) {
        this.$refs.campo.validate(true)
      }
    })
  }

  normalizarValor() {
    if (!this.mascaraAtiva || !this.value) {
      return
    }

    const valorNormalizado = normalizarCarteirinha(this.value, this.mascaraAtiva)

    if (valorNormalizado !== this.value) {
      this.$emit('input', valorNormalizado)
    }
  }
}
